/**
 * The end of a sortie, as a surface a *flying* route can mount: the
 * `SIMEND.DAT` outcome sequence, then MISSION DEBRIEFING.
 *
 * `/career` already reaches `career-screens.ts`'s `debriefingScreen` through its
 * own `SurfaceController`; `/fly` had no way to, and so ended every single
 * mission on a modern HTML card instead — a headline, a bulleted summary, an
 * objectives list and a rounds-fired tally, none of which the original has.
 * This is the missing controller: the same measured screen, the same painter,
 * the same fonts, driven by a route that is otherwise a 3D viewport.
 *
 * **One button, because the original has one.** `PS.EXE`'s debriefing string
 * block ends `ZEPPELINS`, `CONTINUE`, `MISSION DEBRIEFING` — there is no second
 * label in it, and `docs/game/ui-reference/mission-debriefing.png` shows a
 * single centred CONTINUE. The card this replaces had three (`SAVE FLIGHT`,
 * `FLY IT AGAIN`, `CONTINUE`), of which two were port-side. Saving is not lost:
 * the original asks *"Would you like to save this mission to a tape?"* as a
 * dialog **after** the debriefing (`docs/game/ui-reference/dialog-save-to-tape.png`),
 * which is exactly where `onContinue` raises it.
 *
 * **How you came down comes first.** The original shows one of `SIMEND.DAT`'s
 * eleven titled outcome screens before the debriefing
 * (`docs/game/end-of-mission.md`); `/fly` showed none of them at all until G52,
 * so a single mission that ended with the player in a hospital bed reported the
 * same way as one that ended on his own aerodrome. The sequence runs first
 * here, one CONTINUE per page, and the last page's CONTINUE opens the
 * debriefing. A single mission has no career and so no casualty roll — the
 * eight landing predicates on `MissionDebrief` decide the outcome on their own,
 * which `simend.ts` is built to do.
 */

import type { FontId } from "./render/draw-ops.ts";
import type { FontAtlas } from "./render/text-metrics.ts";
import { PANEL_SHEET } from "./render/panel-chrome.ts";
import type { ShellAssetRequest } from "./render/shell-assets.ts";
import type { MenuAction } from "./model/menu-model.ts";
import { debriefingScreen } from "./model/debrief-screens.ts";
import { outcomeScreen, simendSteps, SIMEND_SHEETS, type SimendStep } from "./model/simend-screens.ts";
import type { ShellDialog, ShellScreen } from "./model/shell-screen.ts";
import type { SurfaceController } from "./screen-runtime.ts";
import { simendOutcome, simendSequenceOf, type MissionDebrief } from "@red-opus/engine";

export interface DebriefSurfaceOptions {
  /** Read at paint time, so a debriefing that arrives after mount still draws. */
  readonly debrief: () => MissionDebrief | undefined;
  /** Which service the pilot flies for — decides the header's emblem. */
  readonly nation: () => "german" | "allied";
  /** CONTINUE. Returns a path for the router, or `undefined` to stay put. */
  readonly onContinue: () => string | undefined;
}

/** The one action the screen has. */
const CONTINUE: MenuAction = { kind: "command", command: "debrief:continue" };

/**
 * A `MissionDebrief` with nothing in it, for the frame between mounting and the
 * report arriving. It is never seen in practice — the route mounts this only
 * once a flight is decided — but a surface that can be painted must always have
 * something to paint, and a thrown error inside the painter takes the canvas
 * down rather than showing an empty screen.
 */
const PENDING: MissionDebrief = {
  missionId: "",
  title: "",
  outcome: "inProgress",
  headline: "",
  narrative: [],
  missionScore: 0,
  aircraftLost: 0,
  personalVictories: 0,
  squadronVictories: 0,
  squadronBalloonVictories: 0,
  squadronZeppelinVictories: 0,
  aircraftKills: 0,
  balloonKills: 0,
  zeppelinKills: 0,
  roundsFired: 0,
  hitsScored: 0,
  hitsTaken: 0,
  duration: "0:00",
  victories: [],
  friendlyKills: 0,
  groundTargetsDestroyed: 0,
  friendlyGroundTargetsLost: 0,
  playerGroundTargetsDestroyed: 0,
  shipsSunk: 0,
  raid: undefined,
  playerLost: false,
  playerWounded: false,
  pilotKilled: false,
  overFriendlyTerritory: true,
  landedSafely: false,
  landedAtBase: false,
  gearCollapsed: false,
  killedBy: undefined,
  baledOut: false,
  landedInEnemyTerritory: false,
  objectives: [],
  summary: [],
};

export interface DebriefSurface extends SurfaceController {
  /** Re-reads the report and repaints — the route calls it when one arrives. */
  reopen(): void;
}

export function createDebriefSurface(options: DebriefSurfaceOptions): DebriefSurface {
  let focus = 0;
  let dialog: ShellDialog | undefined;
  /** How far through the outcome sequence CONTINUE has got. At `steps.length` the debriefing is on. */
  let page = 0;
  /** Keyed on the report itself, because `screen()` runs once per repaint and this allocates. */
  let cached: { readonly report: MissionDebrief; readonly steps: readonly SimendStep[] } | undefined;

  const assets: ShellAssetRequest = { images: [], sprites: [PANEL_SHEET, ...SIMEND_SHEETS] };

  function atlas(fonts: ReadonlyMap<FontId, FontAtlas>, id: FontId): FontAtlas {
    const found = fonts.get(id);
    if (!found) throw new Error(`the debriefing screen needs the ${id} atlas`);
    return found;
  }

  function steps(): readonly SimendStep[] {
    const report = options.debrief();
    if (!report) return [];
    if (cached?.report !== report) {
      // `killedBy` travels twice: `simendOutcome` reads it to pick the *named*
      // VANQUISHED! sequence, and the facts fill that sequence's `%a`.
      const facts = report.killedBy === undefined ? {} : { killedBy: report.killedBy };
      cached = { report, steps: simendSteps(simendSequenceOf(simendOutcome(report), facts), options.nation()) };
    }
    return cached.steps;
  }

  return {
    assets,
    screen(fonts: ReadonlyMap<FontId, FontAtlas>): ShellScreen {
      const step = steps()[page];
      if (step) return outcomeScreen(step, atlas(fonts, "FONT1"), CONTINUE, options.nation());
      return debriefingScreen(
        options.debrief() ?? PENDING,
        atlas(fonts, "FONT1"),
        atlas(fonts, "TALK"),
        CONTINUE,
        options.nation(),
      );
    },
    dialog: () => dialog,
    focus: () => focus,
    setFocus: (index: number) => {
      focus = index;
    },
    setDialog: (next: ShellDialog | undefined) => {
      dialog = next;
    },
    run(action: MenuAction | undefined): string | undefined {
      // `cancel` on this screen is CONTINUE too: the original's debriefing has
      // no way out but forward, so Escape must not leave the pilot on a screen
      // with a dead keyboard.
      if (!action) return undefined;
      if (action.kind === "navigate") return action.path;
      if (action.kind === "command" && CONTINUE.kind === "command" && action.command === CONTINUE.command) {
        // Walk the outcome sequence first; only the debriefing's own CONTINUE
        // leaves the surface.
        if (page < steps().length) {
          page += 1;
          return undefined;
        }
        return options.onContinue();
      }
      return undefined;
    },
    typeKey: () => false,
    reopen() {
      focus = 0;
      page = 0;
      dialog = undefined;
    },
  };
}
